import { Color, Spacing } from '@persona-id/design-tokens';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { TRANSITIONS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import Input, { InputProps } from 'design-system/components/Input';
import FormControlProps from 'lib/interfaces/FormControlProps';

const Container = styled('div')`
  display: flex;
  align-items: center;
`;

const ThumbCSS = css`
  background: ${({ theme }) => theme.color.primary.brand};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  border: none;
  box-shadow: none;
  cursor: pointer;
  height: 16px;
  width: 16px;
  &:active {
    transition: ${TRANSITIONS.fast} background;
    background: ${({ theme }) => theme.color.primary.brand};
  }
`;

const TrackCSS = css`
  background: ${Color.CoreSlate300};
  border-radius: 1px;
  border: none;
  box-shadow: none;
  cursor: pointer;
  height: 4px;
  width: 100%;
  &:active {
    transition: ${TRANSITIONS.fast} background;
    background: ${({ theme }) => theme.color.primary.brand200};
  }
`;

/*
 * Note styles need to be separately declared without commas, else browsers
 * will ignore these rules entirely. We also have to prefix these manually
 * because PostCSS doesn't handle these by default, as these aren't CSS-spec
 * properties but internal vendor properties.
 */
const StyledRangeInput = styled('input')`
  -webkit-appearance: none;
  width: 100%;
  margin: 6px 0;

  /* Slider styles */
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    margin-top: -6px;
    ${ThumbCSS}
  }
  &::-moz-range-thumb {
    ${ThumbCSS}
  }
  &::-ms-thumb {
    ${ThumbCSS}
  }

  /* Track styles */
  &::-webkit-slider-runnable-track {
    ${TrackCSS}
  }
  &::-moz-range-track {
    ${TrackCSS}
  }
  &::-ms-track {
    ${TrackCSS}
  }
  &::-ms-fill-lower {
    ${TrackCSS}
  }
  &::-ms-fill-upper {
    ${TrackCSS}
  }
`;

const StyledNumInput = styled(Input)`
  width: 64px;
  text-align: right;
  padding: ${Spacing[1]};
  padding-left: ${Spacing[2]};
  margin-left: ${Spacing[2]};
  flex-shrink: 0;
  &:not(:last-child) {
    margin-right: ${Spacing[1]};
  }
`;

export interface RangeInputProps extends Omit<InputProps, 'onChange'>, FormControlProps {
  min: number;
  max: number;
  value?: string;
  unit?: string;
  onChange?: (value: number) => void;
}

const RangeInput = ({ value, min, max, onChange, unit, ...props }: RangeInputProps) => {
  const [inputValue, setInputValue] = useState(value);

  // Sync stateful input with external state changes
  useEffect(() => setInputValue('' + value), [value]);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      const numValue = +e.target.value;

      /*
       * Only commit value to callback if it is valid. We ignore the empty string in case we ever
       * want to accept negative values, as number inputs treat '-' as ''.
       */
      if (e.target.value !== '' && onChange && numValue >= min && numValue <= max) {
        onChange(numValue);
      }
    },
    [onChange, min, max],
  );

  const handleInputBlur = useCallback(() => {
    if (!onChange) {
      return;
    }

    /*
     * If value is invalid, commit min/max to callback (which will flow back to input).
     * Note we also call setInputValue to prevent the input not updating if value didn't change
     * from the call to onChange().
     */
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (+inputValue < min || inputValue === '') {
      setInputValue('' + min);
      onChange(min);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    } else if (+inputValue > max) {
      setInputValue('' + max);
      onChange(max);
    }
  }, [inputValue, onChange, min, max]);

  return (
    <Container>
      <StyledRangeInput
        type='range'
        value={value}
        min={min}
        max={max}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(+e.target.value)}
      />
      <StyledNumInput
        {...props}
        type='number'
        value={inputValue}
        min={min}
        max={max}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
      />
      {unit && <span>{unit}</span>}
    </Container>
  );
};

export default RangeInput;
